import { Select, type SelectProps } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';

type SelectValue = string | undefined;

type EnsureOption = { value: string; label: string };

type BaseProps = Omit<SelectProps, 'options' | 'loading'> & {
  value?: SelectValue | string[];
  onChange?: (value: SelectValue | string[]) => void;
  /** Keep a selected row visible even if the lookup list has not loaded / misses it. */
  ensureOption?: EnsureOption;
};

function asList<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) return payload as T[];
  if (payload && typeof payload === 'object' && Array.isArray((payload as { data?: unknown }).data)) {
    return (payload as { data: T[] }).data;
  }
  return [];
}

function withEnsureOption(
  options: Array<{ value: string; label: string }>,
  ensureOption?: EnsureOption
): Array<{ value: string; label: string }> {
  if (!ensureOption?.value) return options;
  if (options.some((row) => row.value === ensureOption.value)) return options;
  return [ensureOption, ...options];
}

function personLabel(first?: string | null, last?: string | null, fallback = '—'): string {
  const name = [first, last].filter(Boolean).join(' ').trim();
  return name || fallback;
}

const selectDefaults: Pick<SelectProps, 'showSearch' | 'allowClear' | 'optionFilterProp' | 'style'> = {
  showSearch: true,
  allowClear: true,
  optionFilterProp: 'label',
  style: { width: '100%' },
};

type StudentRow = {
  id: string;
  admission_no?: string;
  first_name?: string;
  last_name?: string | null;
};

export function EducationStudentSelect(props: BaseProps) {
  const { t } = useTranslation('education');
  const query = useQuery({
    queryKey: ['education-lookup-students'],
    queryFn: async () => asList<StudentRow>((await api.get('/education/students')).data),
  });
  const options = useMemo(
    () =>
      (query.data ?? []).map((row) => ({
        value: row.id,
        label: `${row.admission_no || row.id.slice(0, 8)} — ${personLabel(row.first_name, row.last_name)}`,
      })),
    [query.data]
  );
  return (
    <Select
      {...selectDefaults}
      placeholder={t('student', { defaultValue: 'Student' })}
      loading={query.isLoading}
      options={options}
      {...props}
    />
  );
}

type SessionRow = { id: string; code?: string; name?: string; status?: string };

export function EducationSessionSelect(props: BaseProps) {
  const { t } = useTranslation('education');
  const query = useQuery({
    queryKey: ['education-lookup-sessions'],
    queryFn: async () => asList<SessionRow>((await api.get('/education/academic/sessions')).data),
  });
  const options = useMemo(
    () =>
      (query.data ?? []).map((row) => ({
        value: row.id,
        label: `${row.code || row.id.slice(0, 8)} — ${row.name || t('session')}${row.status ? ` (${row.status})` : ''}`,
      })),
    [query.data, t]
  );
  return (
    <Select
      {...selectDefaults}
      placeholder={t('session')}
      loading={query.isLoading}
      options={options}
      {...props}
    />
  );
}

type CampusRow = { id: string; code?: string; name?: string; is_active?: number | boolean };

export function EducationCampusSelect(props: BaseProps) {
  const { t } = useTranslation('education');
  const query = useQuery({
    queryKey: ['education-lookup-campuses'],
    queryFn: async () => asList<CampusRow>((await api.get('/education/campuses')).data),
  });
  const options = useMemo(
    () =>
      (query.data ?? []).map((row) => ({
        value: row.id,
        label: `${row.code || row.id.slice(0, 8)} — ${row.name || t('campus')}${
          row.is_active === 0 || row.is_active === false ? ` (${t('inactive')})` : ''
        }`,
      })),
    [query.data, t]
  );
  return (
    <Select
      {...selectDefaults}
      placeholder={t('campus')}
      loading={query.isLoading}
      options={options}
      notFoundContent={
        query.isError
          ? t('campusLookupDenied', { defaultValue: 'Campus list unavailable for this role' })
          : t('campusEmptyHint', {
              defaultValue: 'No campus yet — create one under Students (New student) or ask an admin',
            })
      }
      {...props}
    />
  );
}

type GradeRow = { id: string; code?: string; name?: string; sequence_no?: number };

export function EducationGradeSelect({ ensureOption, ...props }: BaseProps) {
  const { t } = useTranslation('education');
  const query = useQuery({
    queryKey: ['education-lookup-grades'],
    queryFn: async () => {
      const res = await api.get('/education/academic/grades');
      return asList<GradeRow>(res.data ?? res);
    },
  });
  const options = useMemo(
    () =>
      withEnsureOption(
        (query.data ?? []).map((row) => ({
          value: String(row.id),
          label: `${row.code || String(row.id).slice(0, 8)} — ${row.name || t('grade')}`,
        })),
        ensureOption
      ),
    [query.data, t, ensureOption]
  );
  return (
    <Select
      {...selectDefaults}
      placeholder={t('grade')}
      loading={query.isLoading}
      options={options}
      {...props}
    />
  );
}

type SectionRow = {
  id: string;
  name?: string;
  capacity?: number;
  enrolled_count?: number;
  seats_available?: number;
  session_id?: string;
  grade_level_id?: string;
  grade_code?: string;
  grade_name?: string;
  session_code?: string;
  session_name?: string;
};

export function EducationSectionSelect({
  sessionId,
  gradeLevelId,
  onlyWithSeats,
  excludeSectionId,
  ...props
}: BaseProps & {
  sessionId?: string;
  gradeLevelId?: string;
  onlyWithSeats?: boolean;
  excludeSectionId?: string;
}) {
  const { t } = useTranslation('education');
  const query = useQuery({
    queryKey: ['education-lookup-sections', sessionId, gradeLevelId],
    queryFn: async () =>
      asList<SectionRow>(
        (
          await api.get('/education/academic/sections', {
            params: {
              session_id: sessionId || undefined,
              grade_level_id: gradeLevelId || undefined,
            },
          })
        ).data
      ),
  });
  const options = useMemo(() => {
    let rows = query.data ?? [];
    if (excludeSectionId) rows = rows.filter((row) => row.id !== excludeSectionId);
    if (onlyWithSeats) {
      rows = rows.filter((row) => (row.seats_available ?? (row.capacity ?? 0) - (row.enrolled_count ?? 0)) > 0);
    }
    return rows.map((row) => {
      const seats =
        row.seats_available ?? Math.max(0, (row.capacity ?? 0) - (row.enrolled_count ?? 0));
      const grade = row.grade_code || row.grade_name || '';
      const parts = [
        row.name || t('section'),
        grade,
        `${seats}/${row.capacity ?? '—'} ${t('seatsLeft', { defaultValue: 'seats left' })}`,
      ].filter(Boolean);
      return {
        value: row.id,
        label: parts.join(' · '),
        disabled: onlyWithSeats ? seats <= 0 : false,
      };
    });
  }, [query.data, t, onlyWithSeats, excludeSectionId]);
  return (
    <Select
      {...selectDefaults}
      placeholder={t('section')}
      loading={query.isLoading}
      options={options}
      notFoundContent={
        query.isLoading
          ? t('loading', { defaultValue: 'Loading…' })
          : t('noEligibleSections', {
              defaultValue: 'No matching sections — create them under Academic Sessions for this grade',
            })
      }
      {...props}
    />
  );
}

type QuotaRow = { id: string; code?: string; name?: string; is_active?: number | boolean };

export function EducationQuotaSelect(props: BaseProps) {
  const { t } = useTranslation('education');
  const query = useQuery({
    queryKey: ['education-lookup-quotas'],
    queryFn: async () => asList<QuotaRow>((await api.get('/education/quotas')).data),
  });
  const options = useMemo(
    () =>
      (query.data ?? [])
        .filter((row) => row.is_active == null || Number(row.is_active) === 1)
        .map((row) => ({
          value: row.id,
          label: `${row.code || row.id.slice(0, 8)} — ${row.name || t('quota', { defaultValue: 'Quota' })}`,
        })),
    [query.data, t]
  );
  return (
    <Select
      {...selectDefaults}
      placeholder={t('quota', { defaultValue: 'Quota' })}
      loading={query.isLoading}
      options={options}
      {...props}
    />
  );
}

type HouseRow = { id: string; code?: string; name?: string; is_active?: number | boolean };

export function EducationHouseSelect(props: BaseProps) {
  const { t } = useTranslation('education');
  const query = useQuery({
    queryKey: ['education-lookup-houses'],
    queryFn: async () => asList<HouseRow>((await api.get('/education/houses')).data),
  });
  const options = useMemo(
    () =>
      (query.data ?? [])
        .filter((row) => row.is_active == null || Number(row.is_active) === 1)
        .map((row) => ({
          value: row.id,
          label: `${row.code || row.id.slice(0, 8)} — ${row.name || t('house', { defaultValue: 'House' })}`,
        })),
    [query.data, t]
  );
  return (
    <Select
      {...selectDefaults}
      placeholder={t('house', { defaultValue: 'House' })}
      loading={query.isLoading}
      options={options}
      {...props}
    />
  );
}

type SubjectRow = { id: string; code?: string; name?: string };

export function EducationSubjectSelect(props: BaseProps) {
  const { t } = useTranslation('education');
  const query = useQuery({
    queryKey: ['education-lookup-subjects'],
    queryFn: async () => asList<SubjectRow>((await api.get('/education/academic/subjects')).data),
  });
  const options = useMemo(
    () =>
      (query.data ?? []).map((row) => ({
        value: row.id,
        label: `${row.code || row.id.slice(0, 8)} — ${row.name || t('subject', { defaultValue: 'Subject' })}`,
      })),
    [query.data, t]
  );
  return (
    <Select
      {...selectDefaults}
      placeholder={t('subject', { defaultValue: 'Subject' })}
      loading={query.isLoading}
      options={options}
      notFoundContent={
        query.isLoading
          ? t('loading', { defaultValue: 'Loading…' })
          : t('noSubjectsYet', { defaultValue: 'No subjects yet — add them under Subjects & offerings' })
      }
      {...props}
    />
  );
}

type StaffRow = {
  id: string;
  employee_code?: string;
  first_name?: string;
  last_name?: string;
  is_active?: number | boolean;
};

export function EducationStaffSelect({ ensureOption, ...props }: BaseProps) {
  const { t } = useTranslation('education');
  const query = useQuery({
    queryKey: ['education-lookup-staff'],
    queryFn: async () => {
      const res = await api.get('/education/staff');
      return asList<StaffRow>(res.data ?? res);
    },
  });
  const options = useMemo(
    () =>
      withEnsureOption(
        (query.data ?? [])
          .filter((row) => row.is_active == null || Number(row.is_active) === 1)
          .map((row) => ({
            value: String(row.id),
            label: `${row.employee_code || String(row.id).slice(0, 8)} — ${personLabel(row.first_name, row.last_name)}`,
          })),
        ensureOption
      ),
    [query.data, ensureOption]
  );
  return (
    <Select
      {...selectDefaults}
      placeholder={t('teacher', { defaultValue: 'Teacher' })}
      loading={query.isLoading}
      options={options}
      notFoundContent={
        query.isLoading
          ? t('loading', { defaultValue: 'Loading…' })
          : t('noStaffLinked', { defaultValue: 'No staff linked — link employees under Teacher assignments' })
      }
      {...props}
    />
  );
}

type EnrollmentRow = {
  id: string;
  enrollment_no?: string;
  student_id?: string;
  first_name?: string;
  last_name?: string;
  status?: string;
};

export function EducationEnrollmentSelect({
  studentId,
  ...props
}: BaseProps & { studentId?: string }) {
  const { t } = useTranslation('education');
  const query = useQuery({
    queryKey: ['education-lookup-enrollments'],
    queryFn: async () => asList<EnrollmentRow>((await api.get('/education/enrollments')).data),
  });
  const options = useMemo(() => {
    const rows = (query.data ?? []).filter((row) => !studentId || row.student_id === studentId);
    return rows.map((row) => ({
      value: row.id,
      label: `${row.enrollment_no || row.id.slice(0, 8)} — ${personLabel(row.first_name, row.last_name)}${
        row.status ? ` (${row.status})` : ''
      }`,
    }));
  }, [query.data, studentId]);
  return (
    <Select
      {...selectDefaults}
      placeholder={t('enrollmentNo')}
      loading={query.isLoading}
      options={options}
      {...props}
    />
  );
}

type ApplicationRow = {
  id: string;
  admission_no?: string | null;
  first_name?: string;
  last_name?: string | null;
  status?: string;
};

export function EducationApplicationSelect(props: BaseProps) {
  const { t } = useTranslation('education');
  const query = useQuery({
    queryKey: ['education-lookup-applications'],
    queryFn: async () => asList<ApplicationRow>((await api.get('/education/admissions')).data),
  });
  const options = useMemo(
    () =>
      (query.data ?? []).map((row) => ({
        value: row.id,
        label: `${row.admission_no || row.id.slice(0, 8)} — ${personLabel(row.first_name, row.last_name)}${
          row.status ? ` (${row.status})` : ''
        }`,
      })),
    [query.data]
  );
  return (
    <Select
      {...selectDefaults}
      placeholder={t('application')}
      loading={query.isLoading}
      options={options}
      {...props}
    />
  );
}

type RegisterRow = {
  id: string;
  section_id?: string;
  section_name?: string;
  attendance_date?: string;
  period_code?: string;
  status?: string;
};

export function EducationRegisterSelect(props: BaseProps) {
  const { t } = useTranslation('education');
  const query = useQuery({
    queryKey: ['education-lookup-registers'],
    queryFn: async () => asList<RegisterRow>((await api.get('/education/attendance/registers')).data),
  });
  const options = useMemo(
    () =>
      (query.data ?? []).map((row) => ({
        value: row.id,
        label: [
          row.attendance_date || row.id.slice(0, 8),
          row.section_name || row.section_id?.slice(0, 8),
          row.period_code,
          row.status,
        ]
          .filter(Boolean)
          .join(' · '),
      })),
    [query.data]
  );
  return (
    <Select
      {...selectDefaults}
      placeholder={t('register')}
      loading={query.isLoading}
      options={options}
      {...props}
    />
  );
}

type EmployeeRow = {
  id: string;
  employee_code?: string;
  first_name?: string;
  last_name?: string;
  user_id?: string | null;
};

export function EducationEmployeeSelect({
  onEmployeePicked,
  onChange,
  ...props
}: BaseProps & { onEmployeePicked?: (row: EmployeeRow | undefined) => void }) {
  const { t } = useTranslation('education');
  const query = useQuery({
    queryKey: ['education-lookup-employees'],
    queryFn: async () => asList<EmployeeRow>((await api.get('/education/employees')).data),
  });
  const options = useMemo(
    () =>
      (query.data ?? []).map((row) => ({
        value: row.id,
        label: `${row.employee_code || row.id.slice(0, 8)} — ${personLabel(row.first_name, row.last_name)}`,
      })),
    [query.data]
  );
  return (
    <Select
      {...selectDefaults}
      placeholder={t('employee', { defaultValue: 'Employee' })}
      loading={query.isLoading}
      options={options}
      {...props}
      onChange={(value) => {
        onChange?.(value as SelectValue);
        const row = (query.data ?? []).find((item) => item.id === value);
        onEmployeePicked?.(row);
      }}
    />
  );
}
